import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { FAQ_ITEMS } from "@/lib/constants";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6 },
};

export default function FAQPage() {
  return (
    <>
      <Helmet>
        <title>FAQ | Frequently Asked Questions - Texoma Resorts</title>
        <meta name="description" content="Find answers to common questions about Texoma Resorts, including check-in times, pet policy, booking experiences, cancellation policy, and group bookings." />
      </Helmet>

      <section className="pt-28 pb-12 md:pt-32 md:pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="caption-text text-accent mb-3">Help Center</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-light text-foreground tracking-tight">
              Frequently Asked Questions
            </h1>
          </motion.div>
        </div>
      </section>

      <section data-testid="faq-section" className="pb-16 md:pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp}>
            <Accordion type="single" collapsible className="w-full">
              {FAQ_ITEMS.map((item, i) => (
                <AccordionItem key={item.question} value={`item-${i}`} className="border-b border-border/50">
                  <AccordionTrigger
                    data-testid={`faq-question-${i}`}
                    className="font-heading text-lg font-normal text-foreground hover:text-primary py-5 text-left"
                  >
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="font-body text-sm text-muted-foreground leading-relaxed pb-5">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>

          <motion.div {...fadeUp} className="mt-12 p-8 rounded-lg bg-muted/50 text-center">
            <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
              Still Have Questions?
            </h3>
            <p className="font-body text-sm text-muted-foreground mb-4">
              We are here to help! Reach out and we will get back to you quickly.
            </p>
            <Link to="/contact">
              <Button data-testid="faq-contact" className="bg-primary hover:bg-primary/90 text-white font-body rounded-full px-6">
                Contact Us <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
